import { Box, Card, CardContent, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import QuestionsApi from '../../../../../../apis/QuestionsApi';
import LoadingCircle from '../../../../../../components/LoadingCircle/LoadingCircle';
import UnexpectedError from '../../../../../../components/UnexpectedError/UnexpectedError';
import { AnsweredResponseDto } from '../../../../../../generated-types/answeredResponseDto';
import useApi from '../../../../../../lib/useApi';
import {
  AbstractQuestionWithDis,
  MultipleChoiceQuestionWithDis,
  SelectManyQuestionWithDis,
} from '../../../../../../types/QuestionAndAnswersWithDiscriminator';
import MultipleChoiceQuestion from './MultipleChoiceQuestion';
import SelectManyQuestion from './SelectManyQuestion';

/**
 * The properties for the question card component
 */
interface QuestionCardProps {
  /**
   * The id of the associated quiz module
   */
  moduleId: string;
  /**
   * The number of correctly answered questions the quiz requires in order to be considered complete
   * */
  questionCountMinimum: number;
  /**
   * The initial number of the already correctly answered questions
   */
  initialAnsweredQuestions: number;
  /**
   * Set the associated quiz module as complete
   * @returns Void
   */
  setComplete: () => void;
  /**
   * Set whether the previous question was correct or not.
   * @param _ True, if the previous question was correct. False, otherwise.
   */
  setPrevQuestionCorrect: (_: boolean) => void;
  /**
   * Show feedback whether the question was correctly answered
   */
  showFeedback: () => void;
}

/**
 * The question card component is a wrapper around the main functionality for the quiz. The content
 * of the card will be changed based on the state of the quiz.
 */
const QuestionCard = (props: QuestionCardProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [completedQuestions, setCompletedQuestions] = useState<number>(
    props.initialAnsweredQuestions
  );
  const [currentQuestion, setCurrentQuestion] =
    useState<AbstractQuestionWithDis>();
  const [currentQuestionComponent, setCurrentQuestionComponent] =
    useState<JSX.Element>();
  const questionsApi = useApi(QuestionsApi);
  const [showError, setShowError] = useState(false);

  /**
   * Handle the post answer response. Process the answers and mark the
   * quiz module complete, if needed
   */
  const handlePostAnswer = useCallback(
    (response: AnsweredResponseDto) => {
      setCurrentQuestion(
        response.nextQuestion as unknown as AbstractQuestionWithDis
      );
      setCompletedQuestions(response.totalAnsweredQuestions);
      props.setPrevQuestionCorrect(response.wasCorrect);
      props.showFeedback();

      // If the user has answered enough questions, mark the module as complete
      if (response.totalAnsweredQuestions >= props.questionCountMinimum) {
        props.setComplete();
      }
    },
    [props]
  );

  useEffect(() => {
    setIsLoading(true);
    questionsApi
      .GetNextQuestion(props.moduleId)
      .then((q) => {
        setCompletedQuestions(q.totalAnsweredQuestions);
        setCurrentQuestion(
          q.nextQuestion as unknown as AbstractQuestionWithDis
        );
      })
      .catch(() => {
        setShowError(true);
        setIsLoading(false);
      });
  }, [props.moduleId, questionsApi]);

  useEffect(() => {
    if (currentQuestion) {
      switch (currentQuestion?.$type) {
        case 'multiple-choice':
        case 'true-false':
        case 'all-of-the-above':
          setCurrentQuestionComponent(
            <MultipleChoiceQuestion
              question={
                currentQuestion as unknown as MultipleChoiceQuestionWithDis
              }
              completedQuestions={completedQuestions}
              questionCountMinimum={props.questionCountMinimum}
              isLoading={isLoading}
              handlePostAnswer={handlePostAnswer}
            />
          );
          setIsLoading(false);
          break;
        case 'select-many':
          setCurrentQuestionComponent(
            <SelectManyQuestion
              question={currentQuestion as unknown as SelectManyQuestionWithDis}
              completedQuestions={completedQuestions}
              questionCountMinimum={props.questionCountMinimum}
              isLoading={isLoading}
              handlePostAnswer={handlePostAnswer}
            />
          );
          setIsLoading(false);
          break;
        default:
          // TODO handle unrecognized type
          break;
      }
    }
  }, [
    completedQuestions,
    currentQuestion,
    handlePostAnswer,
    isLoading,
    props.questionCountMinimum,
  ]);

  return (
    <Box position="relative">
      <Card
        data-testid="question-card-id"
        sx={{
          width: '100%',
          filter: isLoading ? 'blur(5px)' : 'initial',
          paddingX: '10px',
        }}
      >
        {showError ? (
          <Box padding="2em">
            <UnexpectedError errorContext="the question" />
          </Box>
        ) : (
          <CardContent
            sx={{
              height: isLoading ? '250px !important' : 'initial',
              paddingBottom: '.5em !important',
            }}
          >
            <Typography marginBottom="25px" variant="h5" component="h5">
              {currentQuestion?.question}
            </Typography>
            {currentQuestionComponent}
          </CardContent>
        )}
      </Card>
      <LoadingCircle isLoading={isLoading} loadingDelayMs={0} />
    </Box>
  );
};

export default QuestionCard;
